import { Injectable } from "@angular/core";
import { validate as uuidValidate } from "uuid";
import { FileUploadsModalService } from "../../modals/file-uploads/file-uploads-modal-service";
import { FileUploadsQuery } from "../state/file-uploads.query";
import { FileTranslationsQuery } from "../state/file-translations.query";
import { ImageAttachment } from "../../features/checklists/checklist.interface";
import { map } from "rxjs/operators";
import { FileTranslation } from "../state/file-translation.model";

@Injectable({ providedIn: "root" })
export class ImageTranslationService {
	constructor(
		private fileUploadsModalService: FileUploadsModalService,
		private fileUploadsQuery: FileUploadsQuery,
		private fileTranslationsQuery: FileTranslationsQuery,
	) {}

	private getUuidImages(attachments: ImageAttachment[]): string[] {
		return attachments.map((attachment) => attachment.url).filter((url) => uuidValidate(url));
	}

	private translateAttachmentUrls(
		attachments: ImageAttachment[],
		translations: FileTranslation[],
	): ImageAttachment[] {
		return attachments.map((attachment: ImageAttachment) => {
			if (uuidValidate(attachment.url)) {
				const translation = translations.find((translation) => translation.id === attachment.url);
				if (!translation) {
					return attachment;
				}

				return {
					url: translation.path,
					cameraRoll: attachment.cameraRoll,
				};
			}
			return attachment;
		});
	}

	async translateImages(attachments: ImageAttachment[]): Promise<ImageAttachment[] | null> {
		const allAttachmentsAreUploaded = await this.handlePendingUploads(attachments);
		if (!allAttachmentsAreUploaded) {
			return null;
		}

		const uuidImages = this.getUuidImages(attachments);
		const translations = await this.fileTranslationsQuery.selectMany(uuidImages).firstAsync();
		return this.translateAttachmentUrls(attachments, translations);
	}

	private async getPendingUploadImageIds(attachments: ImageAttachment[]): Promise<string[]> {
		const uuidImages = this.getUuidImages(attachments);

		if (uuidImages.length === 0) {
			return [];
		}

		return this.fileUploadsQuery
			.selectMany(uuidImages)
			.pipe(map((fileUploads) => fileUploads.map((fileUpload) => fileUpload.id)))
			.firstAsync();
	}

	private async handlePendingUploads(attachments: ImageAttachment[]): Promise<boolean> {
		const pendingUploadImageIds = await this.getPendingUploadImageIds(attachments);

		if (pendingUploadImageIds.length > 0) {
			const modal = await this.fileUploadsModalService.create(pendingUploadImageIds);
			await modal.present();
			await modal.onDidDismiss();

			const stillPendingUploadImageIds = await this.getPendingUploadImageIds(attachments);
			if (stillPendingUploadImageIds.length > 0) {
				return false;
			}
		}

		return true;
	}
}
